import React, { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { AuthChrome } from '../../components/brand/AuthChrome';
import { LogIn } from 'lucide-react';

/** Admin Platform sign-in — email/password only. No Google. No marketing. */
export const AdminLoginPage: React.FC = () => {
  const { login, logout, isAuthenticated, isAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromState = (location.state as { from?: string; reason?: string } | null)?.from;
  const reason = (location.state as { reason?: string } | null)?.reason;
  const nextRaw = params.get('next') || fromState || '/admin';
  const returnTo =
    nextRaw.startsWith('/admin') && !nextRaw.startsWith('/admin/login') ? nextRaw : '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isAuthenticated && isAdmin) {
    return <Navigate to={returnTo} replace />;
  }

  if (isAuthenticated && !isAdmin) {
    return <Navigate to="/customer" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await login(email, password);
      if (next.user.role !== 'platform_admin') {
        await logout();
        setError(
          t(
            'auth.adminOnly',
            'This sign-in is for platform administrators only. Customers use Google Sign-In.'
          )
        );
        return;
      }
      navigate(returnTo, { replace: true });
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setError(t('auth.emailNotVerified', 'Verify your email before signing in.'));
        return;
      }
      setError(err instanceof Error ? err.message : t('auth.failed', 'Login failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthChrome
      title={t('auth.adminSignInTitle', 'Admin Platform')}
      subtitle={t(
        'auth.adminSignInSubtitle',
        'Separate console for BeCoder operators. No marketing surface.'
      )}
      footer={
        <div className="flex flex-wrap justify-between text-xs gap-3 pt-1 border-t border-white/10">
          <Link to="/login" className="text-zinc-400 hover:text-paper transition-colors">
            {t('auth.customerSignInLink', 'Customer Google sign-in')}
          </Link>
        </div>
      }
    >
      {reason === 'forbidden' && (
        <div
          role="status"
          className="text-xs p-3 rounded-md bg-amber-500/10 text-amber-200 border border-amber-500/20"
        >
          {t('auth.forbidden', 'Your account does not have access to that portal.')}
        </div>
      )}

      {error && (
        <p
          role="alert"
          aria-live="assertive"
          className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="admin-email">
            {t('auth.email', 'Email')}
          </label>
          <input
            id="admin-email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bc-auth-input"
          />
        </div>
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="admin-password">
            {t('auth.password', 'Password')}
          </label>
          <input
            id="admin-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bc-auth-input"
          />
        </div>
        <button type="submit" disabled={busy} className="bc-btn-primary w-full !py-3 !min-h-11">
          <LogIn className="w-4 h-4" />
          {busy ? t('common.loading') : t('auth.signIn', 'Sign in')}
        </button>
      </form>
    </AuthChrome>
  );
};
